import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { useCart } from "../lib/CartContext";
import { useSettings } from "../lib/SettingsContext";
import { normalizePhone } from "../lib/whatsapp";
import { ShoppingBag, User, LogOut, LayoutDashboard, Menu, X, MessageCircle } from "lucide-react";
import { Button } from "./ui/button";

export default function Navbar() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const { settings } = useSettings();
  const nav = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const links = [
    { to: "/", label: "Inicio" },
    { to: "/tienda", label: "Tienda" },
    { to: "/como-funciona", label: "Cómo funciona" },
    { to: "/contacto", label: "Contacto" },
  ];

  const doLogout = async () => {
    await logout();
    nav("/");
  };

  const brandName = settings?.brand_name || "PRINTLAB";
  const accent = settings?.brand_accent || "#FF3B30";
  const logoHeight = Math.max(24, Math.min(96, Number(settings?.logo_height) || 32));

  // WhatsApp CTA
  const waPhone = normalizePhone(settings?.company_whatsapp);
  const waText = encodeURIComponent(`Hola ${brandName}, quisiera más información sobre sus productos.`);
  const waHref = waPhone ? `https://wa.me/${waPhone}?text=${waText}` : null;

  return (
    <header className="sticky top-0 z-40 bg-white border-b border-neutral-200">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-2 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="nav-logo">
          {settings?.logo_url ? (
            <img
              src={settings.logo_url}
              alt={brandName}
              className="w-auto object-contain"
              style={{ height: `${logoHeight}px`, maxHeight: "96px", maxWidth: "260px" }}
            />
          ) : (
            <>
              <div
                className="bg-black flex items-center justify-center"
                style={{ width: `${logoHeight}px`, height: `${logoHeight}px` }}
              >
                <span className="font-display text-white leading-none" style={{ fontSize: `${logoHeight * 0.55}px` }}>{brandName[0]}</span>
              </div>
              <span className="font-display tracking-tighter" style={{ fontSize: `${logoHeight * 0.65}px` }}>
                {brandName}<span style={{ color: accent }}>.</span>
              </span>
            </>
          )}
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`text-sm font-medium hover:text-[#FF3B30] transition-colors ${
                location.pathname === l.to ? "text-[#FF3B30]" : "text-neutral-700"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          {waHref && (
            <a
              href={waHref}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium transition-colors"
              data-testid="nav-whatsapp-btn"
              aria-label="Chatear por WhatsApp"
            >
              <MessageCircle className="w-4 h-4" />
              <span className="hidden lg:inline">WhatsApp</span>
            </a>
          )}
          <Link to="/carrito" className="relative p-2 hover:bg-neutral-100 transition-colors" data-testid="nav-cart-button">
            <ShoppingBag className="w-5 h-5" />
            {count > 0 && (
              <span
                data-testid="nav-cart-count"
                className="absolute -top-0.5 -right-0.5 bg-[#FF3B30] text-white text-[10px] font-bold min-w-[18px] h-[18px] px-1 flex items-center justify-center"
              >
                {count}
              </span>
            )}
          </Link>
          {user ? (
            <div className="hidden md:flex items-center gap-2">
              {(user.role === "admin" || user.role === "operator") && (
                <Button asChild variant="ghost" size="sm" className="brutal-btn" data-testid="nav-admin-btn">
                  <Link to="/admin">
                    <LayoutDashboard className="w-4 h-4 mr-2" /> Panel
                  </Link>
                </Button>
              )}
              <Button asChild variant="ghost" size="sm" className="brutal-btn" data-testid="nav-profile-btn">
                <Link to="/perfil">
                  <User className="w-4 h-4 mr-2" /> {user.name?.split(" ")[0] || "Perfil"}
                </Link>
              </Button>
              <Button onClick={doLogout} variant="ghost" size="sm" className="brutal-btn" data-testid="nav-logout-btn">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          ) : (
            <div className="hidden md:flex items-center gap-2">
              <Button asChild variant="ghost" size="sm" className="brutal-btn" data-testid="nav-login-btn">
                <Link to="/login">Entrar</Link>
              </Button>
              <Button asChild size="sm" className="brutal-btn bg-black hover:bg-[#FF3B30] text-white" data-testid="nav-register-btn">
                <Link to="/registro">Crear cuenta</Link>
              </Button>
            </div>
          )}
          <button
            data-testid="nav-mobile-toggle"
            className="md:hidden p-2 hover:bg-neutral-100"
            onClick={() => setMobileOpen((v) => !v)}
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t border-neutral-200 bg-white">
          <div className="px-6 py-4 flex flex-col gap-3">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setMobileOpen(false)}
                className="text-sm font-medium py-2"
                data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
              >
                {l.label}
              </Link>
            ))}
            {waHref && (
              <a
                href={waHref}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 mt-1 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium"
                data-testid="nav-mobile-whatsapp"
                onClick={() => setMobileOpen(false)}
              >
                <MessageCircle className="w-4 h-4" /> Chatear por WhatsApp
              </a>
            )}
            {user ? (
              <>
                {(user.role === "admin" || user.role === "operator") && (
                  <Link to="/admin" onClick={() => setMobileOpen(false)} className="text-sm font-medium py-2">Panel Admin</Link>
                )}
                <Link to="/perfil" onClick={() => setMobileOpen(false)} className="text-sm font-medium py-2">Mi Perfil</Link>
                <button onClick={doLogout} className="text-sm font-medium py-2 text-left">Cerrar sesión</button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setMobileOpen(false)} className="text-sm font-medium py-2">Entrar</Link>
                <Link to="/registro" onClick={() => setMobileOpen(false)} className="text-sm font-medium py-2">Crear cuenta</Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
